import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PartyPopper } from "lucide-react";
import { SiteLayout, useSettings } from "@/components/SiteLayout";
import { useI18n } from "@/lib/i18n";
import { api, rp, fmtDate } from "@/lib/api";
import { PayOnline } from "@/components/PayOnline";
import { ManualBca } from "@/components/ManualBca";
import { logError } from "@/lib/log";

const STATUS_KEYS = {
  Inquiry: "bookingStatusInquiry",
  "Waiting Confirmation": "bookingStatusWaitingConfirmation",
  Confirmed: "bookingStatusConfirmed",
  "Waiting Payment": "bookingStatusWaitingPayment",
  "DP Paid": "bookingStatusDepositPaid",
  Paid: "bookingStatusPaid",
  Preparing: "bookingStatusPreparing",
  "Out for Delivery": "bookingStatusOutForDelivery",
  "Rented / Playing": "bookingStatusRented",
  "Waiting for Pickup": "bookingStatusWaitingPickup",
  Returned: "bookingStatusReturned",
  Inspection: "bookingStatusInspection",
  Completed: "bookingStatusCompleted",
  Cancelled: "bookingStatusCancelled",
};

export default function Confirmation() {
  const { code } = useParams();
  const { t, lang } = useI18n();
  const s = useSettings();
  const [b, setB] = useState(null);
  useEffect(() => {
    api.get(`/public/booking/${code}`).then((r) => setB(r.data)).catch((e) => logError("Booking not found", e));
  }, [code]);
  if (!b) return <SiteLayout><div className="mx-auto max-w-2xl px-5 py-24 text-ink-soft">{t("loading")}</div></SiteLayout>;

  const msg = "";
  return (
    <SiteLayout>
      <div className="mx-auto max-w-2xl px-5 py-16 lm-fade">
        <PartyPopper className="text-lm-gold" size={40} />
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tighter" data-testid="confirmation-title">{t("bookingReceived")}</h1>
        <div className="mt-8 lm-card rounded-xl p-6 space-y-2.5 text-sm">
          <R l={t("bookingId")} v={<span data-testid="confirmation-code" className="font-semibold">{b.booking_code}</span>} />
          <R l={t("customer")} v={b.customer_name} />
          <R l={t("rentalDate")} v={fmtDate(b.rental_date, lang)} />
          <R l={t("bookingStart")} v={b.start_time} />
          <R l={t("package")} v={b.package_name_snapshot} />
          <R l={t("bookingQuantity")} v={`${b.quantity} ${t("bookingUnit")}`} />
          <div className="h-px bg-black/10 my-1" />
          <R l={t("rentalTotal")} v={rp(b.final_rental_total)} />
          {b.promo_discount > 0 && <R l={`Promo ${b.promo_code || ""}`} v={`-${rp(b.promo_discount)}`} />}
          {b.loyalty_discount > 0 && <R l={`${b.loyalty_tier} Member -${b.loyalty_discount_percent}%`} v={`-${rp(b.loyalty_discount)}`} />}
          {b.benefit_discount > 0 && <R l={b.benefit_label || t("benefit")} v={`-${rp(b.benefit_discount)}`} />}
          {b.deposit_amount > 0 && <R l={t("refundableDeposit")} v={rp(b.deposit_amount)} />}
          <R l={t("totalPayment")} v={<span className="font-display text-xl text-lm-red" data-testid="confirmation-total">{rp(b.total_payment)}</span>} />
          <R l={t("bookingStatus")} v={<span className="px-2.5 py-1 rounded-full text-xs bg-amber-100 text-amber-800">{t(STATUS_KEYS[b.booking_status])}</span>} />
        </div>
        <div className="mt-6" data-testid="payment-instructions">
          {s?.online_payment_public_enabled && (
            <PayOnline booking={b} onPaid={() => api.get(`/public/booking/${code}`).then((r) => setB(r.data))} />
          )}
          <ManualBca code={b.booking_code} />
          <p className="mt-4 text-xs text-ink-soft" data-testid="payment-note">
            {t("bookingPaymentNote")}
          </p>
        </div>

        <div className="mt-6"><Link to="/check-booking" className="text-sm text-lm-red hover:underline">{t("checkBooking")} →</Link></div>

        <div className="mt-10 rounded-xl border border-lm-gold/40 bg-lm-gold/5 p-6" data-testid="join-member-cta">
          <h3 className="font-display text-xl tracking-tight">{t("joinMemberTitle")}</h3>
          <p className="mt-2 text-sm text-ink-soft">{t("joinMemberSub")}</p>
          <Link to="/member/login?mode=signup" data-testid="join-member-btn"
            className="mt-5 inline-flex h-11 items-center px-6 rounded-full bg-ink text-cream text-sm hover:bg-lm-red transition-colors">
            {t("createMemberAccount")}
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}

const R = ({ l, v }) => (
  <div className="flex justify-between gap-4"><span className="text-ink-soft">{l}</span><span className="text-right">{v}</span></div>
);
